// salesApi.ts — the product a sales order line sells.
// Shapes read off api/src/sales/sales-router.js and sales-service.js
// (getOrderById, validateProduct). A stock line may name a drone model
// (part_catalog_id) and one of its active parts list's variants; the API
// refuses anything else with a 400 code: not_a_product, unknown_variant
// (with `allowed`), product_on_non_stock_line, variant_without_product,
// model_required (a `product: 'model'` line with none), product_on_this_line.

import { productCode, productName } from '../Parts/bomsApi';
import type { ProductSummary } from '../Parts/bomsApi';
import { ITEM_TYPES } from './itemTypes';
import type { ItemType } from './itemTypes';

/** The product fields every line read carries. All null when the line names no product. */
export interface LineProduct {
  part_catalog_id: number | null;
  variant: string | null;
  product_code?: string | null;
  product_name?: string | null;
}

/** One product the line editor offers. */
export interface ProductOption {
  id: number;
  code: string;
  label: string;
  variants: string[];
  /** A drone model. A drone line offers only these; any product with a list may go on another stock line. */
  is_model: boolean;
}

/** Active products only: a retired product is still readable on old lines, never picked for new ones. */
export const productOptionsFrom = (models: ProductSummary[]): ProductOption[] =>
  models
    .filter((m) => m.is_active)
    .map((m) => ({
      id: m.id,
      code: productCode(m),
      label: productName(m),
      variants: m.variants || [],
      is_model: m.model_code !== null,
    }));

/** Only a stock line sells a product. */
export const isStockItem = (itemType: string): boolean =>
  ITEM_TYPES.find((t) => t.value === itemType)?.kind === 'stock';

/** One option in the line editor's Item dropdown. */
export interface ItemChoice {
  key: string;
  label: string;
  itemType: string;
  productId: number | null;
}

/** Item types in catalog order, a `product: 'model'` type expanded into one choice per drone model. */
export const itemChoices = (types: ItemType[], products: ProductOption[]): ItemChoice[] =>
  types.flatMap((t) => (t.product === 'model'
    ? products.filter((p) => p.is_model).map((p) => ({ key: `${t.value}:${p.id}`, label: p.label, itemType: t.value, productId: p.id }))
    : [{ key: t.value, label: t.label, itemType: t.value, productId: null }]));
